import PropTypes from 'prop-types';
import { useMemo } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Slider } from '@hivebrite/design-system/Slider';
import { useTheme } from '@hivebrite/design-system/Theme';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';

import TopicItem from './TopicItem';

const SLIDER_CARD_HEIGHT = {
  s: 189,
  m: 124,
  l: 168,
  xl: 180,
};

const TopicsAsLane = ({
  loading,
  topics,
  total,
  loadNextPage,
  updateTopicApproval,
  LoadingItemComponent,
}) => {
  const { currentNetwork } = useDefaultAppContexts();

  const theme = useTheme();

  const configDescriptionIsNotVisible = !Number(
    currentNetwork?.features?.topics?.config
      ?.description_visible_in_frontoffice,
  );

  // We can only hide the empty description filler block in topic card
  // if LD flag is enabled and all topics in are missing their description
  const canRemoveDescriptionPlaceholder = useMemo(() => {
    if (topics.length) {
      return !topics.some(({ description }) => description);
    }

    // Rely on network settings by default.
    return configDescriptionIsNotVisible;
  }, [topics]);

  const renderLoadingItemComponent = (props) => (
    <LoadingItemComponent
      {...props}
      noDescriptionPlaceholder={canRemoveDescriptionPlaceholder}
    />
  );

  return (
    <Box
      flexDirection="column"
      width="100%"
      px={theme.grid.gridGap}
      // Compensate inner paddings that for some reason are set in <Slider />.
      my="-6px"
      data-testid="topics-as-lane"
    >
      <Slider
        loading={loading}
        items={topics}
        perPageBreakpoints={{
          s: 1,
          m: 3,
          l: 3,
          xl: 4,
        }}
        total={total}
        renderLoadingItemComponent={renderLoadingItemComponent}
        renderItemComponent={(topic) => (
          <TopicItem
            canRemoveDescriptionPlaceholder={canRemoveDescriptionPlaceholder}
            currentNetwork={currentNetwork}
            topic={topic}
            updateTopicApproval={updateTopicApproval}
          />
        )}
        onLoadMore={loadNextPage}
        pictureHeight={SLIDER_CARD_HEIGHT}
        pictureSpace={14}
      />
    </Box>
  );
};

TopicsAsLane.protoTypes = {
  loading: PropTypes.bool.isRequired,
  topics: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
  cardsPerRow: PropTypes.object.isRequired,
  loadNextPage: PropTypes.func.isRequired,
  updateTopicApproval: PropTypes.func,
  LoadingItemComponent: PropTypes.elementType.isRequired,
};

export default TopicsAsLane;
